import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { updateBirthday } from 'api/jalapeno';
import { SUBSCRIPTION_QUERY_KEY } from 'api/queries/useSubscriptionQuery';
import Button from 'honeydew/Button';
import TextField from 'honeydew/TextField';
import useBirthdayField from 'hooks/useBirthdayField';
import profileStyles from 'pages/account/Profile/styles';
import { birthdayValidation, drinkingAgeValidation, getLongBirthday } from 'utils/birthdayUtils';

/**
 * @param {Object} props
 * @param {string} [props.birthday=''] - The birthday string
 * @param {boolean} [props.continueCtaCopy=false] - Whether to show continue CTA copy
 * @param {number|null} [props.customerID=null] - The customer ID
 * @param {boolean} [props.editingMode=false] - Whether the component is in editing mode
 * @param {Function|null} [props.onClickHandler=null] - Click handler function
 * @param {number|null} [props.subscriptionID=null] - The subscription ID
 * @param {boolean} [props.validateDrinkingAge=false] - Whether to validate drinking age (21+)
 */
export default function Birthday({
    birthday = '',
    continueCtaCopy = false,
    customerID = null,
    editingMode = false,
    onClickHandler = null,
    subscriptionID = null,
    validateDrinkingAge = false,
}) {
    const [isEditing, setIsEditing] = useState(editingMode);
    const mounted = useRef(false);
    const currentBirthday = useRef(birthday);
    const toggleEditingOff = () => setIsEditing(false);

    useEffect(() => {
        if (birthday !== currentBirthday && mounted.current && !editingMode) {
            setIsEditing(false);
            currentBirthday.current = birthday;
        }

        mounted.current = true;
    }, [birthday, currentBirthday]);

    return isEditing ? (
        <div className={profileStyles.editContainer}>
            <Form
                customerID={customerID}
                birthday={birthday ?? ''}
                continueCtaCopy={continueCtaCopy}
                onSuccess={toggleEditingOff}
                validateDrinkingAge={validateDrinkingAge}
            />
            <button type="button" className={profileStyles.cancel} onClick={onClickHandler || toggleEditingOff}>
                Cancel
            </button>
        </div>
    ) : (
        <div className={profileStyles.displayContainer}>
            <div>
                <h4 className="font-grotesk-bold">Birthday</h4>
                {subscriptionID && (
                    <p className="mt-10">
                        {birthday ? getLongBirthday(birthday) : 'Add your birthday for wine purchases and promotions.'}
                    </p>
                )}
            </div>
            <button className={profileStyles.edit} type="button" onClick={() => setIsEditing(true)}>
                Edit
            </button>
        </div>
    );
}

/**
 * @param {Object} props
 * @param {string} props.birthday - The birthday string
 * @param {boolean} [props.continueCtaCopy=false] - Whether to show continue CTA copy
 * @param {number} props.customerID - The customer ID
 * @param {Function|null} [props.onSuccess=null] - Success callback function
 * @param {boolean} [props.validateDrinkingAge=false] - Whether to validate drinking age (21+)
 */
function Form({ birthday, continueCtaCopy = false, customerID, onSuccess = null, validateDrinkingAge = false }) {
    const queryClient = useQueryClient();
    const { birthdayFieldProps, birthdayFieldValue } = useBirthdayField(birthday);
    const [isBusy, setIsBusy] = useState(false);
    const [error, setError] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);

    const getButtonCopy = () => {
        if (continueCtaCopy) return isBusy ? 'Saving...' : 'Continue';
        return isBusy ? 'Saving...' : 'Save';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setErrorMsg(null);
        setIsBusy(true);

        try {
            birthdayValidation.validateSync(birthdayFieldValue);
        } catch (err) {
            setError(true);
            setErrorMsg(err.errors[0]);
            setIsBusy(false);
            return;
        }

        if (validateDrinkingAge) {
            try {
                drinkingAgeValidation.validateSync(birthdayFieldValue);
            } catch (err) {
                setError(true);
                setErrorMsg(err.errors[0]);
                setIsBusy(false);
                return;
            }
        }

        try {
            await updateBirthday(customerID, { dob: birthdayFieldValue });
            await queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_QUERY_KEY] });
            setIsBusy(false);
            onSuccess?.();
        } catch (err) {
            setError(true);
            setErrorMsg('Oops! The entered birthday could not be updated. Please try again.');
            setIsBusy(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="block grow">
            <fieldset className="space-y-5">
                <TextField
                    name="dob"
                    id="dob"
                    value={birthdayFieldValue}
                    {...birthdayFieldProps}
                    error={error}
                    errorMsg={errorMsg}
                />
                <Button type="submit" disabled={isBusy}>
                    {getButtonCopy()}
                </Button>
            </fieldset>
        </form>
    );
}
